<template>
  <div class="form-select-element-wrapper">
    <FormLabel :element="element" :labelStyle="labelStyle" />

    <FormDescription :element="element" :labelStyle="labelStyle" />

    <GeneralDropdown
      className="form-select-field"
      :style="getSelectBoxStyle"
      ref="filterDropRef"
      :dropdownId="`input-${element.id}`"
      uniqueReferenceId="filterDropRef"
      :disabled="disabled || element.settings.disabled"
      :required="element.settings.required"
      @onDropdownOpen="onDropdownOpen"
      @onDropdownClose="onDropdownClose"
    >
      <template v-slot:selectFilter>
        <input
          ref="selectInputRef"
          placeholder="wähle eine Option"
          :style="getInputFieldStyle"
          :value="getSelectValue"
          :name="element.name"
          :disabled="disabled"
          :required="element.settings.required"
          @input="onHandleInput"
          @invalid="onHandleInvalid"
          class="selected-input fw-normal fs-6 w-100"
        />
        <img
          class="error-icon position-absolute top-50"
          :src="require('@/assets/img/error-circle-exclamation.svg')"
        />
      </template>
      <template v-slot:optionData>
        <b-dropdown-item
          v-for="(option, index) in getFilterOptionList"
          :key="index"
          @click="onSelectOption(option)"
          :class="{ 'selected-option': option.value === element.value }"
        >
          {{ option.label }}
        </b-dropdown-item>
        <b-dropdown-item
          v-if="getFilterOptionList.length === 0"
          class="no-option"
        >
          Keine Einträge gefunden
        </b-dropdown-item>
      </template>
    </GeneralDropdown>
  </div>
</template>

<script>
import GeneralDropdown from '@/components/core/general-dropdown.vue';
import { DEFAULT_FORM_STYLES } from '@/utils/constants/WorkFlowConstants';
import { showInputInvalidCustomMessage } from '@/utils/helpers';
import FormLabel from '@/components/FormBuilder/core/FormLabel.vue';
import FormDescription from '@/components/FormBuilder/core/FormDescription.vue';
export default {
  name: 'FormSelectElement',
  data() {
    return {
      optionSearchValue: null,
    };
  },
  props: {
    element: {
      type: Object,
      required: true,
    },
    labelStyle: { type: Object, default: () => {} },
    fieldStyle: { type: Object, default: () => {} },
    brandedStyle: { type: Object, default: () => {} },
    isPreview: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  methods: {
    onDropdownOpen() {
      if (!this.element.settings.searchEnabled) return;
      setTimeout(() => {
        this.$refs.selectInputRef?.focus();
        const cursorPosition = this.getSelectValue?.length || 0;
        this.$refs.selectInputRef?.setSelectionRange(
          cursorPosition,
          cursorPosition
        );
      }, 0);
    },
    onDropdownClose() {
      if (this.optionSearchValue) {
        const option = this.element.options.find(
          (op) => op.label == this.optionSearchValue
        );
        if (option) {
          this.onSelectOption(option);
        } else {
          this.$set(this.element, 'value', null);
        }
      }
      this.optionSearchValue = null;
      if (this.$refs.selectInputRef)
        this.onHandleInvalid({ target: this.$refs.selectInputRef });
    },
    onSelectOption(option) {
      this.$set(this.element, 'value', option.value);
      this.optionSearchValue = option.label;
    },
    onHandleInput(event) {
      this.optionSearchValue = event.target.value;
      if (!this.$refs?.filterDropRef?.$refs?.filterDropRef?.isDropdownOpen) {
        this.$refs?.filterDropRef?.$refs?.filterDropRef?.show();
      }
    },
    onHandleInvalid(event) {
      showInputInvalidCustomMessage(event, 'Bitte wählen Sie eine Option aus');
    },
  },
  computed: {
    showRequiredMark() {
      return (
        !!this.element?.settings?.required &&
        !this.element?.settings?.disabled &&
        String(this.element?.label || '').trim().length > 0
      );
    },
    getFilterOptionList() {
      if (this.optionSearchValue) {
        return this.element.options.filter((op) =>
          op.label.toLowerCase().includes(this.optionSearchValue.toLowerCase())
        );
      }
      return this.element.options;
    },
    getSelectValue() {
      return this.optionSearchValue !== null
        ? this.optionSearchValue
        : this.element.options.find((op) => op.value == this.element.value)
            ?.label || '';
    },
    getInputFieldStyle() {
      return {
        color: this.fieldStyle?.textColor || DEFAULT_FORM_STYLES.inputColor,
        fontFamily:
          this.brandedStyle?.fontType || DEFAULT_FORM_STYLES.inputFont,
      };
    },
    getSelectBoxStyle() {
      return {
        borderColor: this.fieldStyle?.borderColor,
        outlineColor: this.fieldStyle?.outlineColor,
        borderRadius: this.fieldStyle?.borderRadius,
      };
    },
    placeholderStyle() {
      return {
        color:
          this.fieldStyle?.placeholderColor ||
          DEFAULT_FORM_STYLES.placeHolderColor,
        fontFamily:
          this.brandedStyle?.fontType || DEFAULT_FORM_STYLES.placeholderFont,
      };
    },
  },
  components: { GeneralDropdown, FormLabel, FormDescription },
};
</script>

<style scoped>
/* dropdown style */
.form-select-element-wrapper .form-select-field {
  width: 100%;
  max-width: 100%;
  border: 0.7px solid #dddddd;
  border-radius: 10px;
}
.form-select-element-wrapper .form-label .required-star {
  color: #ff0000;
  margin-left: 4px;
  display: inline-block;
  line-height: 1;
  transform: scale(0.8);
  transform-origin: left center;
  vertical-align: baseline;
}
.form-select-element-wrapper .form-select-field.show,
.form-select-element-wrapper ::v-deep .form-select-field:focus {
  box-shadow: none;
  border-color: transparent !important;
  outline: 2.2px solid #000000;
  outline-offset: -2.2px;
}

.form-select-element-wrapper .form-select-field .selected-input {
  line-height: 36px;
  height: 62px;
  font-family: 'OpenSans-Regular';
  letter-spacing: -0.02em;
  color: #222222;
}

.form-select-element-wrapper .form-select-field .selected-input::placeholder {
  font-weight: 600;
  font-family: v-bind('placeholderStyle.fontFamily');
  color: v-bind('placeholderStyle.color');
}

.form-select-element-wrapper
  .form-select-field
  .selected-input:-webkit-autofill {
  box-shadow: 0 0 0 1000px white inset !important;
  -webkit-box-shadow: 0 0 0 1000px white inset !important;
  -webkit-text-fill-color: v-bind('getInputFieldStyle.color');
}

.form-select-element-wrapper .form-select-field .selected-input:disabled {
  cursor: not-allowed;
  opacity: 0.6;
  color: #fbfbfb !important;
  border-color: #ededed !important;
}
.form-select-element-wrapper .form-select-field .selected-input:focus {
  outline: none;
}

.form-select-element-wrapper .form-select-field ::v-deep .dropdown-toggle {
  padding: 0 23px;
  height: 65px;
  box-shadow: none;
  justify-content: space-between;
}

.form-select-element-wrapper .form-select-field ::v-deep .dropdown-toggle-icon {
  padding: 0px;
  height: 18px;
}
.form-select-element-wrapper ::v-deep .dropdown-menu .selected-option a {
  background: #f0f0f0 !important;
}
.form-select-element-wrapper ::v-deep .dropdown-menu.show {
  width: calc(100% - 6px) !important;
  max-height: 230px;
  top: 2px !important;
  left: 0px !important;
  box-shadow: 0px 0px 8px 0.5px #00000033;
  border-radius: 10px;
  padding: 6px 5px;
  margin-left: 6px;
  display: flex;
  flex-direction: column;
}

.form-select-element-wrapper ::v-deep .dropdown-menu.show li a {
  font-weight: 400;
  border-radius: 4px;
  font-size: 16px;
  letter-spacing: -0.02em;
  line-height: 29px;
  padding: 7px 18px;
  font-family: v-bind('getInputFieldStyle.fontFamily');
  color: v-bind('getInputFieldStyle.color');
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
}
.form-select-element-wrapper ::v-deep .dropdown-menu.show li:hover a {
  background-color: #f4f4f4;
}
.form-select-element-wrapper::v-deep .dropdown-menu .no-option {
  pointer-events: none;
}
.form-select-element-wrapper ::v-deep .form-select-field .error-icon {
  right: 23px;
  transform: translateY(-50%);
  cursor: pointer;
  color: #ff0000;
  width: 21px;
  height: 21px;
  display: none;
}
.was-submitted
  .form-select-element-wrapper
  ::v-deep
  .form-select-field:has(.selected-input:invalid)
  .error-icon {
  display: block;
}

.was-submitted
  .form-select-element-wrapper
  ::v-deep
  .form-select-field:has(.selected-input:invalid)
  .dropdown-toggle {
  padding-right: 53px;
}
.was-submitted .form-input-element-wrapper .form-input:invalid {
  outline: 2.2px solid #ff0000 !important;
  background-color: #ff000008;
  border-color: transparent !important;
  outline-offset: -2.2px;
}

.was-submitted
  .form-select-element-wrapper
  ::v-deep
  .form-select-field:has(.selected-input:invalid) {
  outline-offset: -2.2px;
  outline: 2.2px solid #ff0000 !important;
}
.form-select-element-wrapper .form-label {
  margin-bottom: 18px !important;
}
</style>
